import type {
  InventoryHireHirerKind,
  InventoryHireStatus,
} from '../types/organization';

export interface InventoryHireHirerJoinFields {
  directMemberId: string | null;
  directMemberFirstName: string | null;
  directMemberLastName: string | null;
  directMemberPhone: string | null;
  bookingMemberId: string | null;
  bookingMemberFirstName: string | null;
  bookingMemberLastName: string | null;
  bookingMemberPhone: string | null;
  guestFirstName: string | null;
  guestLastName: string | null;
  guestPhone: string | null;
  guestEmail: string | null;
  hirerName: string | null;
}

export interface ResolvedInventoryHireHirer {
  hirerKind: InventoryHireHirerKind;
  hirerDisplayName: string | null;
  hirerPhone: string | null;
  hirerMemberId: string | null;
}

function formatPersonName(firstName: string | null, lastName: string | null): string | null {
  const name = [firstName, lastName].filter(Boolean).join(' ').trim();
  return name || null;
}

/** Resolve display hirer from inventory hire joins (member, court-booking guest, or walk-up). */
export function resolveInventoryHireHirer(
  fields: InventoryHireHirerJoinFields
): ResolvedInventoryHireHirer {
  if (fields.directMemberId) {
    return {
      hirerKind: 'member',
      hirerMemberId: fields.directMemberId,
      hirerDisplayName: formatPersonName(fields.directMemberFirstName, fields.directMemberLastName),
      hirerPhone: fields.directMemberPhone,
    };
  }

  if (fields.bookingMemberId) {
    return {
      hirerKind: 'member',
      hirerMemberId: fields.bookingMemberId,
      hirerDisplayName: formatPersonName(fields.bookingMemberFirstName, fields.bookingMemberLastName),
      hirerPhone: fields.bookingMemberPhone,
    };
  }

  const guestName = formatPersonName(fields.guestFirstName, fields.guestLastName);
  if (guestName || fields.guestEmail) {
    return {
      hirerKind: 'guest',
      hirerMemberId: null,
      hirerDisplayName: guestName ?? fields.guestEmail,
      hirerPhone: fields.guestPhone,
    };
  }

  if (fields.hirerName?.trim()) {
    return {
      hirerKind: 'walk_up',
      hirerMemberId: null,
      hirerDisplayName: fields.hirerName.trim(),
      hirerPhone: null,
    };
  }

  return {
    hirerKind: 'unknown',
    hirerMemberId: null,
    hirerDisplayName: null,
    hirerPhone: null,
  };
}

/** Statuses that reserve inventory units against availability. */
export const INVENTORY_HIRE_RESERVING_STATUSES: readonly InventoryHireStatus[] = [
  'pending',
  'confirmed',
] as const;

/** Unpaid checkout holds (`pending`) are not consumer reservations until confirmed. */
export function isInventoryHireCalendarVisible(status: InventoryHireStatus): boolean {
  return status === 'confirmed';
}

/** Member portal lists show confirmed and completed hires only — not unpaid checkout holds. */
export function isMemberPortalInventoryHireVisible(status: InventoryHireStatus): boolean {
  return status === 'confirmed' || status === 'completed';
}

/**
 * Per-hour hire total prorated by booking duration.
 * Formula: round(hirePriceCents * quantity * minutes / 60)
 */
export function calculateInventoryHireTotal(
  hirePriceCents: number,
  quantity: number,
  startAt: Date,
  endAt: Date
): number {
  if (hirePriceCents <= 0 || quantity <= 0) return 0;
  const minutes = Math.max(0, (endAt.getTime() - startAt.getTime()) / (60 * 1000));
  if (minutes <= 0) return 0;
  return Math.round((hirePriceCents * quantity * minutes) / 60);
}

/**
 * Minutes between two instants (non-negative).
 */
export function inventoryHireDurationMinutes(startAt: Date, endAt: Date): number {
  return Math.max(0, (endAt.getTime() - startAt.getTime()) / (60 * 1000));
}

/**
 * Available units in a window = owned quantity minus overlapping reserved hires.
 */
export function resolveAvailableQuantity(
  ownedQuantity: number,
  overlappingReservedQuantity: number
): number {
  return Math.max(0, ownedQuantity - overlappingReservedQuantity);
}

/**
 * True when stock tracking is enabled and quantity is at or below the threshold.
 */
export function isLowStock(
  quantity: number,
  lowStockThreshold: number | null | undefined,
  tracksStock: boolean
): boolean {
  if (!tracksStock) return false;
  if (lowStockThreshold == null) return false;
  return quantity <= lowStockThreshold;
}

/**
 * Sum hire quantities from overlapping hires (each hire contributes its quantity).
 */
export function sumOverlappingHireQuantities(
  hires: Array<{ quantity: number; status: InventoryHireStatus }>,
  reservingStatuses: readonly InventoryHireStatus[] = INVENTORY_HIRE_RESERVING_STATUSES
): number {
  return hires
    .filter((h) => reservingStatuses.includes(h.status))
    .reduce((sum, h) => sum + h.quantity, 0);
}
